<template>
  <TransitionRoot appear :show="true" as="template">
    <Dialog as="div" class="relative z-10">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="
                w-full
                max-w-xl
                transform
                overflow-hidden
                rounded-xl
                bg-white
                text-left
                align-middle
                shadow-xl
                transition-all
                p-4
              "
            >
              <DialogOverlay class="p-5 pt-5">
                <div class="col-span-2 block mb-4">
                  <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300" />
                    </div>
                    <div class="relative flex justify-between">
                      <h2 class="font-bold pr-3 bg-white col-md-6 text-xl text-vice-teal-900">
                        {{ isEditing ? 'Edit group' : 'New group' }}
                      </h2>
                      <span class="p-1 pl-3 bg-white cursor-pointer" @click="close">
                        <CrossGrayIcon class="h-6 w-6" />
                      </span>
                    </div>
                  </div>
                </div>

                <div class="container max-w-full justify-between text-sm">
                  <form @submit.prevent="submit" class="p-2 relative text-left">
                    <div>
                      <jet-label for="group_name" value="Group name *" />
                      <jet-input
                        id="group_name"
                        type="text"
                        class="mt-1 block w-full"
                        v-model="form.name"
                        required
                        autofocus
                      />
                    </div>

                    <div class="mt-4">
                      <jet-label value="Students" />
                      <input
                        type="text"
                        v-model="studentSearch"
                        placeholder="Search students..."
                        class="
                          mt-2
                          block
                          w-full
                          border-gray-300
                          focus:border-indigo-300
                          focus:ring focus:ring-indigo-200 focus:ring-opacity-50
                          rounded-md
                          shadow-sm
                          sm:text-sm
                        "
                      />
                      <div class="mt-2 max-h-56 overflow-y-auto border border-gray-200 rounded-md">
                        <div
                          v-if="!filteredStudents.length"
                          class="px-3 py-4 text-sm text-gray-500 text-center"
                        >
                          No students found.
                        </div>
                        <label
                          v-for="student in filteredStudents"
                          :key="student.id"
                          class="
                            flex
                            items-center
                            gap-2
                            px-3
                            py-2
                            text-sm
                            border-b border-gray-100
                            last:border-b-0
                            cursor-pointer
                            hover:bg-gray-50
                          "
                        >
                          <input
                            type="checkbox"
                            :value="student.id"
                            v-model="selectedUserIds"
                            class="
                              rounded
                              border-gray-300
                              text-vice-teal-600
                              focus:ring-vice-teal-500
                            "
                          />
                          <span class="text-vice-teal-900">{{ student.name }}</span>
                          <span class="text-gray-400">{{ student.email }}</span>
                          <span
                            v-if="student.group_id && student.group_id !== groupId"
                            class="ml-auto text-xxs text-gray-400 whitespace-nowrap"
                          >
                            in {{ student.group_name }}
                          </span>
                        </label>
                      </div>
                      <p class="mt-1 text-xs text-gray-500">
                        {{ selectedUserIds.length }} student
                        {{ selectedUserIds.length === 1 ? '' : 's' }}
                        selected
                      </p>
                    </div>

                    <div v-if="error" class="mt-3 text-sm text-red-600">
                      {{ error }}
                    </div>

                    <div class="flex mt-8 gap-3">
                      <jet-button
                        :class="{ 'opacity-60': submitting || !form.name }"
                        :disabled="submitting || !form.name"
                      >
                        {{ submitting ? 'Saving...' : isEditing ? 'Save changes' : 'Create group' }}
                      </jet-button>
                      <button
                        type="button"
                        @click="close"
                        class="
                          inline-flex
                          items-center
                          px-4
                          py-2
                          border border-gray-300
                          rounded-md
                          shadow-sm
                          text-sm
                          font-medium
                          text-gray-700
                          bg-white
                          hover:bg-gray-50
                          focus:outline-none
                          focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                        "
                      >
                        CANCEL
                      </button>
                    </div>
                  </form>
                </div>
              </DialogOverlay>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  Dialog,
  DialogOverlay,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import axios from 'axios';
import JetLabel from '@/Jetstream/Label.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetButton from '@/Jetstream/Button.vue';
import CrossGrayIcon from '@/Components/Icons/CrossGrayIcon.vue';

export default {
  name: 'GroupFormModal',
  props: {
    universityId: {
      type: [Number, String],
      required: true,
    },
    group: {
      type: Object,
      default: null,
    },
    students: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'saved'],
  components: {
    JetButton,
    JetLabel,
    JetInput,
    CrossGrayIcon,
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogOverlay,
  },

  data() {
    return {
      form: {
        name: this.group?.name ?? '',
      },
      selectedUserIds: this.students
        .filter((student) => student.group_id === this.group?.id)
        .map((student) => student.id),
      studentSearch: '',
      submitting: false,
      error: null,
    };
  },

  computed: {
    isEditing() {
      return !!this.group;
    },
    groupId() {
      return this.group?.id ?? null;
    },
    filteredStudents() {
      const query = this.studentSearch.trim().toLowerCase();
      if (!query) return this.students;
      return this.students.filter(
        (student) =>
          student.name?.toLowerCase().includes(query) ||
          student.email?.toLowerCase().includes(query),
      );
    },
  },

  methods: {
    async submit() {
      if (!this.form.name) return;

      this.submitting = true;
      this.error = null;

      try {
        const payload = { name: this.form.name, user_ids: this.selectedUserIds };
        const response = this.isEditing
          ? await axios.post(this.route('group.update', this.group.id), payload)
          : await axios.post(this.route('group.store', this.universityId), payload);

        this.$emit('saved', response.data);
      } catch (error) {
        this.error =
          error.response?.data?.error ??
          error.response?.data?.errors?.name?.[0] ??
          'Something went wrong while saving the group.';
      } finally {
        this.submitting = false;
      }
    },

    close() {
      this.$emit('close');
    },
  },
};
</script>
